import { useJobStatusTracker } from '@/composables/use-job-status-tracker';

/**
 * Factory for switching status composables. Captures the shared `useJobStatusTracker`
 * wiring once and lets each variant curry in its own config — e.g. the status endpoint,
 * which key of the job-status payload it reads, how per-item progress details are
 * extracted from polling vs. push payloads, and the name under which those details
 * are returned.
 *
 * @param {object} config
 * @param {(scenarioId: string) => string} config.statusUrl - builds the polling status URL
 * @param {(jobStatuses: object) => object} config.getJobStatus - selects this job's status from the scenario's `jobs`
 * @param {(statusData: object) => Array} config.getPollingDetails - extracts per-item details from a polling response
 * @param {(status: object) => Array} config.getPushDetails - extracts per-item details from a Dapr-pushed status
 * @param {string} config.detailsKey - property name under which the per-item details are returned
 * @returns {(args: { getScenarioId: () => string, onTerminal: Function }) => object} a `use*SwitchingStatus` composable
 */
export function createSwitchingStatusComposable({
  statusUrl,
  getJobStatus,
  getPollingDetails,
  getPushDetails,
  detailsKey,
}) {
  return function useSwitchingStatus({ getScenarioId, onTerminal }) {
    const { serviceJobDetails, serviceJobProgress, start, stop } = useJobStatusTracker({
      getScenarioId,
      statusUrl,
      getJobStatus,
      onTerminal,
      getPollingDetails,
      getPushDetails,
    });

    return { [detailsKey]: serviceJobDetails, serviceJobProgress, start, stop };
  };
}
